import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import heroImage from "@/assets/hero-smoke.jpg";

const HeroSection = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const scrollToProducts = () => {
    document.getElementById("collection")?.scrollIntoView({
      behavior: "smooth"
    });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img src={heroImage} alt="Bait Al-Teeb luxury incense" className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay-base" />
        <div className="absolute inset-0 hero-overlay-sides" />
      </div>

      {/* Animated smoke overlay elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl smoke-animation" />
        <div className="absolute top-1/3 left-1/3 w-64 h-64 bg-primary/3 rounded-full blur-3xl smoke-animation" style={{ animationDelay: "2s" }} />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}>
          <span className="inline-block px-4 py-2 border border-primary/30 text-primary text-sm tracking-[0.3em] uppercase mb-8">
            {t.hero.badge}
          </span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.4 }} className="text-5xl md:text-7xl lg:text-8xl font-serif text-gradient-gold mb-4 arabic">
          {t.hero.brandArabic}
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.6 }} className="text-xl md:text-2xl font-serif text-amber-100 dark:text-foreground/80 tracking-widest mb-4">
          {t.hero.brandEnglish}
        </motion.p>

        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8 }} className="max-w-lg mx-auto mb-12 leading-relaxed text-center text-amber-200/80 dark:text-secondary">
          {t.hero.tagline}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1 }} className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button variant="bronze" size="xl" onClick={scrollToProducts}>
            {t.hero.exploreCollection}
          </Button>
          <Button variant="luxuryOutline" size="xl" className="border-amber-200/50 text-amber-100 hover:bg-amber-200/10 dark:border-primary/30 dark:text-foreground" onClick={() => navigate("/story")}>
            {t.hero.ourStory}
          </Button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }} className="w-6 h-10 border-2 border-primary/50 rounded-full flex items-start justify-center p-1">
            <div className="w-1.5 h-2.5 bg-primary rounded-full" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
